import {Task} from "@megaphonenpm/mputils/src/Task.js";
import {
  CHANGE, COMPLETE, FAIL
} from '@megaphonenpm/mputils/src/constants.js';
import {areJSONEqual, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {log} from '@megaphonenpm/mputils/src/logfunctions.js';
import {get} from 'lodash-es';
import {UpdateWebPollTask} from './UpdateWebPollTask.js';
import {WebPollService} from './WebPollService.js';
import {SceneService} from './SceneService.js';
import {PRODUCTNAME_SCOREBOARD} from './constants.js';

/**
 * Loads all web polls for the current playlist, and assigns each loaded web poll to the supplied
 * dataStore.
 */
export class LoadWebPollsTask extends Task {
  constructor (dataStore) {
    super();
    this.name = 'LOAD_WEBPOLLS';
    this.numWebPollsToLoad = 0;
    this.numWebPollLoadAttemptsResolved = 0;
    this.dataStore = dataStore;

    this.contentChangedPollIDs = [];
    this.stateChangedPollIDs = [];
    this.resultsChangedPollIDs = [];
  }

  start () {
    super.start();

    let localWebPollService = new WebPollService();
    let webpollsToLoad = [];
    let scenes = this.dataStore.playlist.scenes;
    if (notNil(scenes)) {
      scenes.forEach(scene => {
        let webpollID = get(scene, 'content.src.id');
        let sceneType = get(scene, 'type');
        let screenProductName = new SceneService().getScreenProductName(scene);
        // Load any scene with an external src id that is NOT a scoreboard. E.g., web polls
        // and predictions.
        if (notNil(webpollID) && screenProductName !== PRODUCTNAME_SCOREBOARD) {
          webpollsToLoad.push({webpollID: webpollID, sceneType: sceneType});
        }
      });
    }


    this.numWebPollsToLoad = webpollsToLoad.length;
    this.numWebPollLoadAttemptsResolved = 0;

    if (this.numWebPollsToLoad === 0) {
      log('No web polls in current playlist. No web polls loaded.');
      this.done();
    } else {
      log(`Loading ${this.numWebPollsToLoad} web polls and/or predictions...`);
      webpollsToLoad.forEach(webpollToLoad => {
        let contentName = localWebPollService.getContentName(webpollToLoad.sceneType);
        let updateWebPollTask = new UpdateWebPollTask(webpollToLoad.webpollID, this.dataStore, webpollToLoad.sceneType);
        updateWebPollTask.on(COMPLETE, () => {
          log(`Loaded ${contentName} [${webpollToLoad.webpollID}]`);
          this.numWebPollLoadAttemptsResolved++;
          this.handleWebPollLoadAttemptResolved();
        });
        updateWebPollTask.on(CHANGE, (e) => {
          if (e.detail.currentUpdatedAt !== e.detail.newUpdatedAt) {
            // Poll content changed
            log(`Content changed for ${contentName} [${webpollToLoad.webpollID}]`);
            this.contentChangedPollIDs.push(e.detail.loadedWebPollID);
          }

          if (e.detail.currentState !== e.detail.newState) {
            // Poll state changed
            log(`State changed for ${contentName} [${webpollToLoad.webpollID}]`);
            this.stateChangedPollIDs.push(e.detail.loadedWebPollID);
          }

          if (!areJSONEqual(e.detail.currentResults, e.detail.newResults)) {
            // Poll results changed
            log(`Results changed for ${contentName} [${webpollToLoad.webpollID}]`);
            this.resultsChangedPollIDs.push(e.detail.loadedWebPollID);
          }
        });
        updateWebPollTask.on(FAIL, () => {
          log(`Failed to load ${contentName} [${webpollToLoad.webpollID}]`);
          this.numWebPollLoadAttemptsResolved++;
          this.handleWebPollLoadAttemptResolved();
        });
        updateWebPollTask.start();
      });
    }
  }

  handleWebPollLoadAttemptResolved () {
    if (this.numWebPollLoadAttemptsResolved === this.numWebPollsToLoad) {
      log('All web polls and/or predictions loaded.');
      this.done();
    }
  }
}
